import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
    Alert,
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Switch,
    Tabs,
    Tab,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    OutlinedInput,
    Stack,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

import {
    IconSearch,
    IconPlus,
    IconX,
    IconBuildingCommunity,
    IconHierarchy3,
    IconShield,
    IconChevronRight,
    IconEdit,
    IconTrash,
    IconUserPlus,
    IconBriefcase,
    IconCircleCheck,
    IconArrowLeft
} from '@tabler/icons';

import Snackbar from '@mui/material/Snackbar';

import { IconUsersGroup, IconSparkles } from '@tabler/icons-react';
import api from '../../../../services/api';
import { motion } from 'framer-motion';
import { useHistory, useParams } from 'react-router-dom';

// import axios from 'axios';

const MotionCard = motion(Card);

// const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// const api = axios.create({
//     baseURL: API_URL
// });

// api.interceptors.request.use((config) => {
//     const token = localStorage.getItem('access_token');

//     if (token) {
//         config.headers.Authorization = `Bearer ${token}`;
//     }

//     return config;
// });

const TEAM_TYPES = {
    support: {
        label: 'SAC',
        color: '#2563eb',
        gradient: 'linear-gradient(135deg,#2563eb,#60a5fa)',
        icon: IconShield
    },

    financial: {
        label: 'Financeiro',
        color: '#16a34a',
        gradient: 'linear-gradient(135deg,#16a34a,#4ade80)',
        icon: IconBriefcase
    },

    retention: {
        label: 'Retenção',
        color: '#ea580c',
        gradient: 'linear-gradient(135deg,#ea580c,#fb923c)',
        icon: IconUsersGroup
    }
};

const DEFAULT_TEAM = {
    name: '',
    description: '',
    members: []
};

const DEFAULT_GROUP = {
    name: '',
    description: ''
};

const ManageTeams = () => {
    const theme = useTheme();

    const [teams, setTeams] = useState([]);

    const [groups, setGroups] = useState([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');

    const history = useHistory();

    const [status, setStatus] = useState({
        type: '',
        message: ''
    });

    const [openTeamModal, setOpenTeamModal] = useState(false);

    const [openGroupModal, setOpenGroupModal] = useState(false);

    const [selectedTeam, setSelectedTeam] = useState(null);

    const [teamForm, setTeamForm] = useState(DEFAULT_TEAM);

    const [groupForm, setGroupForm] = useState(DEFAULT_GROUP);

    const [openAlert, setOpenAlert] = useState(false);
    const [msgAlert, setMsgAlert] = useState('');
    const [severityAlert, setSeverityAlert] = useState('info');

    const [users, setUsers] = useState([]);

    const [userSearch, setUserSearch] = useState('');

    const [appId, setAppId] = useState(null);

    const [adicionarMembros, setAdicionarMembros] = useState(true);

    const initialMembersRef = useRef([]);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadData();
        listUsers();
    }, [appId]);

    const loadData = async () => {
        setLoading(true);

        try {
            // const [teamsResponse, groupsResponse] = await Promise.all([api.get('v1/teams/'), api.get('v1/team-groups/')]);
            const teamsResponse = await api.get(`v1/apps/${appId}/teams/`);

            setTeams(teamsResponse.data?.teams || []);

            setGroups([]);
        } catch (error) {
            console.error(error);

            // setStatus({
            //     type: 'error',
            //     message: 'Erro ao carregar equipes.'
            // });

            setAlert('error', 'Erro ao carregar equipes.');
        } finally {
            setLoading(false);
        }
    };

    const filteredTeams = useMemo(() => {
        if (!search.trim()) {
            return teams;
        }

        return teams.filter(
            (team) =>
                team.name?.toLowerCase().includes(search.toLowerCase()) || team.description?.toLowerCase().includes(search.toLowerCase())
        );
    }, [teams, search]);

    const filteredUsers = useMemo(() => {
        const q = userSearch.toLowerCase();
        return users.filter(
            (user) => `${user.first_name} ${user.last_name}`.toLowerCase().includes(q) || user.email?.toLowerCase().includes(q)
        );
    }, [users, userSearch]);

    const handleAdicionarMembros = () => {
        setAdicionarMembros(true);
    };

    const handleToggleMember = (user) => {
        const exists = teamForm.members.find((u) => u.id === user.id);

        if (exists) {
            setTeamForm({ ...teamForm, members: teamForm.members.filter((u) => u.id !== user.id) });
            return;
        }

        setTeamForm({ ...teamForm, members: [...teamForm.members, user] });
    };

    const handleOpenCreateTeam = () => {
        setSelectedTeam(null);
        initialMembersRef.current = [];
        setTeamForm(DEFAULT_TEAM);
        setOpenTeamModal(true);
    };

    const handleOpenEditTeam = async (team) => {
        setSelectedTeam(team);
        initialMembersRef.current = [];

        setTeamForm({
            name: team.name || '',
            description: team.description || '',
            members: []
        });

        setOpenTeamModal(true);

        try {
            const res = await api.get(`v1/apps/${appId}/teams/${team.id}/members/`);
            // Store full record so we have the TeamMember.id for deletion
            const rawMembers = res.data || [];
            initialMembersRef.current = rawMembers.map((m) => ({
                memberId: m.id,
                ...(m.user_detail || { id: m.user })
            }));
            setTeamForm((prev) => ({ ...prev, members: initialMembersRef.current }));
        } catch {
            // members will stay empty, non-fatal
        }
    };

    const syncMembers = async (teamId, members) => {
        const selectedUserIds = new Set(members.map((u) => u.id));
        const existingUserIds = new Set(initialMembersRef.current.map((u) => u.id));

        // Add newly selected members
        for (const user of members) {
            if (!existingUserIds.has(user.id)) {
                try {
                    await api.post(`v1/apps/${appId}/teams/${teamId}/members/`, { user: user.id, role: 'member' });
                } catch {
                    // ignore
                }
            }
        }

        // Remove deselected members
        for (const initial of initialMembersRef.current) {
            if (!selectedUserIds.has(initial.id)) {
                try {
                    await api.delete(`v1/apps/${appId}/teams/${teamId}/members/${initial.memberId}/`);
                } catch {
                    // ignore
                }
            }
        }
    };

    const handleSaveTeam = async () => {
        try {
            const { members, ...teamData } = teamForm;

            if (selectedTeam) {
                await api.patch(`v1/apps/${appId}/teams/${selectedTeam.id}/`, teamData);
                await syncMembers(selectedTeam.id, members);
                setAlert('success', 'Equipe atualizada.');
            } else {
                if (teamForm.name === '') {
                    setAlert('error', 'Nome da equipe é obrigatório.');
                    return;
                }

                if (teamForm.description === '') {
                    setAlert('error', 'Descrição da equipe é obrigatório.');
                    return;
                }

                const res = await api.post(`v1/apps/${appId}/teams/`, teamData);
                const newTeamId = res.data?.team;
                if (newTeamId) {
                    await syncMembers(newTeamId, members);
                }
                setAlert('success', 'Equipe criada.');
            }

            setOpenTeamModal(false);

            loadData();
        } catch (error) {
            console.error(error);

            setAlert('error', 'Erro ao salvar equipe.');
        }
    };

    const handleDeleteTeam = async (id) => {
        const confirmDelete = window.confirm('Deseja excluir esta equipe?');

        if (!confirmDelete) {
            return;
        }

        try {
            await api.delete(`v1/apps/${appId}/teams/${id}/`);

            setTeams((prev) => prev.filter((team) => team.id !== id));

            setAlert('success', 'Equipe removida.');
        } catch (error) {
            console.error(error);

            setAlert('error', 'Erro ao excluir equipe.');
        }
    };

    const listUsers = async () => {
        try {
            const res = await api.get('users/');
            setUsers(res.data || []);
        } catch (err) {
            setAlert('error', 'Erro ao listar usuários.');
        }
    };

    const setAlert = (severity, text) => {
        setSeverityAlert(severity);
        setMsgAlert(text);
        setOpenAlert(true);
    };

    const handleClickAlert = () => {
        setOpenAlert(true);
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenAlert(false);
    };

    return (
        <Box>
            {/* HERO */}
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '32px',
                    marginTop: 2,
                    p: {
                        xs: 3,
                        md: 5
                    },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)'
                            : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 340,
                        height: 340,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.16))',
                        top: -120,
                        right: -100,
                        filter: 'blur(60px)'
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Chip
                        icon={<IconSparkles size={14} />}
                        label="Organização Inteligente"
                        sx={{
                            mb: 2,
                            fontWeight: 700
                        }}
                    />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: '2rem',
                                md: '3rem'
                            },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Gerenciar Equipes
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 760,
                            lineHeight: 1.8,
                            mb: 4
                        }}
                    >
                        Crie equipes e organize seus atendentes entre múltiplos departamentos ao mesmo tempo.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <OutlinedInput
                                fullWidth
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar equipe..."
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                }
                                sx={{
                                    height: 56,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Button
                                    variant="contained"
                                    // startIcon={<IconPlus size={18} />}
                                    startIcon={<IconHierarchy3 size={18} />}
                                    onClick={handleOpenCreateTeam}
                                    sx={{
                                        height: 56,
                                        px: 3,
                                        borderRadius: '18px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                                    }}
                                >
                                    Nova Equipe
                                </Button>

                                {/* <Button
                                    variant="outlined"
                                    startIcon={<IconHierarchy3 size={18} />}
                                    onClick={() => setOpenGroupModal(true)}
                                    sx={{
                                        height: 56,
                                        px: 3,
                                        borderRadius: '18px',
                                        textTransform: 'none',
                                        fontWeight: 700
                                    }}
                                >
                                    Novo Grupo
                                </Button> */}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {openAlert && (
                <Snackbar
                    open={openAlert}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseAlert} severity={severityAlert} variant="filled" sx={{ width: '100%' }}>
                        {msgAlert}
                    </Alert>
                </Snackbar>
            )}

            {/* TEAMS */}
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 800,
                    mb: 3
                }}
            >
                Equipes
            </Typography>

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 10
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredTeams.map((team, index) => {
                        const config = TEAM_TYPES[team.type] || TEAM_TYPES.support;

                        const Icon = config.icon;

                        return (
                            <Grid item xs={12} md={6} lg={4} key={team.id}>
                                <MotionCard
                                    initial={{
                                        opacity: 0,
                                        y: 20
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0
                                    }}
                                    transition={{
                                        delay: index * 0.03
                                    }}
                                    whileHover={{
                                        y: -5
                                    }}
                                    sx={{
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        border:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(15,23,42,0.06)',
                                        background: theme.palette.mode === 'dark' ? '#111827' : '#fff'
                                    }}
                                >
                                    <CardActionArea onClick={() => handleOpenEditTeam(team)}>
                                        <CardContent
                                            sx={{
                                                p: 3
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    mb: 2
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 64,
                                                        height: 64,
                                                        borderRadius: '18px',
                                                        background: config.gradient,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: `0 10px 25px ${config.color}33`
                                                    }}
                                                >
                                                    <Icon size={30} color="#fff" />
                                                </Box>

                                                <Chip
                                                    label={config.label}
                                                    sx={{
                                                        background: config.gradient,
                                                        color: '#fff',
                                                        fontWeight: 700
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 800,
                                                    mb: 1
                                                }}
                                            >
                                                {team.name}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    mb: 2,
                                                    lineHeight: 1.7
                                                }}
                                            >
                                                {team.description}
                                            </Typography>

                                            <Divider
                                                sx={{
                                                    my: 2
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                }}
                                            >
                                                <AvatarGroup max={4}>
                                                    {(team.members || []).map((member) => (
                                                        <Avatar key={member.id} src={member.avatar}>
                                                            {member.name?.charAt(0)}
                                                        </Avatar>
                                                    ))}
                                                </AvatarGroup>

                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {team.members?.length} membros
                                                </Typography>
                                            </Box>

                                            <Divider
                                                sx={{
                                                    my: 2
                                                }}
                                            />

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    Abrir equipe
                                                </Typography>

                                                <IconChevronRight size={18} />
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* TEAM MODAL */}

            <Dialog
                open={openTeamModal}
                onClose={() => setOpenTeamModal(false)}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: '28px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}
                    >
                        <IconUsersGroup size={22} />

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800
                            }}
                        >
                            {selectedTeam ? 'Editar Equipe' : 'Nova Equipe'}
                        </Typography>
                    </Box>

                    <IconButton onClick={() => setOpenTeamModal(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Nome da equipe"
                                value={teamForm.name}
                                onChange={(e) =>
                                    setTeamForm({
                                        ...teamForm,
                                        name: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        {/* <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                select
                                label="Tipo"
                                value={teamForm.type}
                                onChange={(e) =>
                                    setTeamForm({
                                        ...teamForm,
                                        type: e.target.value
                                    })
                                }
                            >
                                <MenuItem value="support">SAC</MenuItem>

                                <MenuItem value="financial">Financeiro</MenuItem>

                                <MenuItem value="retention">Retenção</MenuItem>
                            </TextField>
                        </Grid> */}

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Descrição"
                                value={teamForm.description}
                                onChange={(e) =>
                                    setTeamForm({
                                        ...teamForm,
                                        description: e.target.value
                                    })
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    mb: 2
                                }}
                            >
                                Membros da Equipe
                            </Typography>

                            <Box
                                sx={{
                                    p: 3,
                                    borderRadius: '20px',
                                    border: theme.palette.mode === 'dark' ? '1px dashed rgba(255,255,255,0.12)' : '1px dashed #cbd5e1',
                                    textAlign: 'center'
                                }}
                            >
                                <Button
                                    startIcon={<IconUserPlus size={18} />}
                                    variant="outlined"
                                    onClick={handleAdicionarMembros}
                                    sx={{
                                        borderRadius: '14px',
                                        textTransform: 'none'
                                    }}
                                >
                                    Adicionar membros
                                </Button>

                                {adicionarMembros === true && (
                                    <>
                                        {/* <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 2
                                            }}
                                        >
                                            Destinatários
                                        </Typography> */}

                                        {/* <Tabs value={recipientMode} onChange={(_, value) => setRecipientMode(value)}>
                                        <Tab value="sistema" label="Contatos do sistema" />
                                        <Tab value="csv" label="Planilha CSV" />
                                    </Tabs> */}

                                        <>
                                            <OutlinedInput
                                                fullWidth
                                                placeholder="Buscar usuários..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <IconSearch size={18} />
                                                    </InputAdornment>
                                                }
                                                sx={{
                                                    mb: 2,
                                                    mt: 2,
                                                    borderRadius: '16px'
                                                }}
                                            />

                                            <Paper
                                                sx={{
                                                    maxHeight: 300,
                                                    overflow: 'auto',
                                                    borderRadius: '20px'
                                                }}
                                            >
                                                <List>
                                                    {filteredUsers.map((user) => {
                                                        const selected = teamForm.members.find((u) => u.id === user.id);

                                                        return (
                                                            <ListItem
                                                                key={user.id}
                                                                secondaryAction={
                                                                    <Switch
                                                                        checked={!!selected}
                                                                        onChange={() => handleToggleMember(user)}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemAvatar>
                                                                    <Avatar src={user.avatar_img}>{user.first_name?.charAt(0)}</Avatar>
                                                                </ListItemAvatar>

                                                                <ListItemText
                                                                    primary={`${user.first_name || ''} ${user.last_name || ''}`.trim()}
                                                                    secondary={user.email}
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Paper>
                                        </>
                                    </>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3,
                        justifyContent: 'space-between'
                    }}
                >
                    {selectedTeam ? (
                        <Button
                            color="error"
                            startIcon={<IconTrash size={18} />}
                            onClick={() => {
                                setOpenTeamModal(false);

                                handleDeleteTeam(selectedTeam.id);
                            }}
                            sx={{
                                borderRadius: '14px',
                                textTransform: 'none'
                            }}
                        >
                            Excluir
                        </Button>
                    ) : (
                        <Box />
                    )}

                    <Button
                        variant="contained"
                        startIcon={selectedTeam ? <IconEdit size={18} /> : <IconPlus size={18} />}
                        onClick={handleSaveTeam}
                        sx={{
                            borderRadius: '16px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        {selectedTeam ? 'Salvar Alterações' : 'Criar Equipe'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* GROUPS */}
            {/* <Box sx={{ mb: 5 }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 800,
                        mb: 3
                    }}
                >
                    Grupos
                </Typography>

                <Grid container spacing={2}>
                    {groups.map((group) => (
                        <Grid item xs={12} md={4} key={group.id}>
                            <Card
                                sx={{
                                    borderRadius: '22px',
                                    border:
                                        theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255,255,255,0.06)'
                                            : '1px solid rgba(15,23,42,0.06)',
                                    background: theme.palette.mode === 'dark' ? '#111827' : '#fff'
                                }}
                            >
                                <CardContent>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 2
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: '18px',
                                                background: 'linear-gradient(135deg,#7c3aed,#2563eb)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <IconHierarchy3 size={28} color="#fff" />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 800
                                                }}
                                            >
                                                {group.name}
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                {group.description}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Chip
                                        icon={<IconCircleCheck size={14} />}
                                        label="Grupo ativo"
                                        color="success"
                                        sx={{
                                            fontWeight: 700
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box> */}

            {/* GROUP MODAL */}
            {/* <Dialog
                open={openGroupModal}
                onClose={() => setOpenGroupModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: '26px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}
                    >
                        <IconBuildingCommunity size={22} />

                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800
                            }}
                        >
                            Novo Grupo
                        </Typography>
                    </Box>

                    <IconButton onClick={() => setOpenGroupModal(false)}>
                        <IconX size={18} />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Nome do grupo"
                            value={groupForm.name}
                            onChange={(e) =>
                                setGroupForm({
                                    ...groupForm,
                                    name: e.target.value
                                })
                            }
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Descrição"
                            value={groupForm.description}
                            onChange={(e) =>
                                setGroupForm({
                                    ...groupForm,
                                    description: e.target.value
                                })
                            }
                        />
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        pb: 3
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={18} />}
                        onClick={handleCreateGroup}
                        sx={{
                            borderRadius: '16px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        Criar Grupo
                    </Button>
                </DialogActions>
            </Dialog> */}
        </Box>
    );
};

export default ManageTeams;
